import React, { createContext, useContext, useEffect, useState } from 'react';
import useArrayState from '../../hooks/useArrayState';
import {
  API_CreamTransaction,
  API_GetBalance,
  oneEther,
  TransactionStatuses
} from 'shared';
import useAlert from '../alert';
import useUserData from '../userData';
import { SetState } from '../../interfaces/SetState';
import fetcher from '../../utils/fetcher';
import useWeb3 from '../web3';
import Web3 from 'web3';

type BalanceResponse = {
  balance: number;
  pendingIncomeBalance: number;
  pendingOutcomeBalance: number;
};

type BuyCreamState = {
  cream: string;
  eth: number;
  stopLoading: () => void;
  onFinish: () => void;
};

interface ICream {
  balance: number;
  pendingIncomeBalance: number | undefined;
  pendingOutcomeBalance: number | undefined;
  balanceLoading: boolean;
  getBalance: () => Promise<BalanceResponse | null>;

  txHistory: API_CreamTransaction[];
  txHistoryLoading: boolean;
  getTransactionsHistory: () => void;
  updatePendingTransaction: (
    hash: string,
    stopLoading?: () => any
  ) => Promise<TransactionStatuses>;
  updatePendingLoading: boolean;
  setUpdatePendingLoading: SetState<boolean>;
  buyCream: (state: BuyCreamState) => void;
  sellCream: (
    amount: number,
    onFinish: () => void,
    stopLoading: () => void
  ) => void;
}

// @ts-ignore
const CreamContext = createContext<ICream>();

interface Props {
  children: React.ReactNode;
}

export const CreamContextProvider = ({ children }: Props) => {
  const alert = useAlert();
  const { loggedIn } = useUserData();
  const { connect } = useWeb3();

  //BALANCE
  const [balance, setBalance] = useState<number>(-1);
  const [pendingIncomeBalance, setPendingIncomeBalance] = useState<
    number | undefined
  >(undefined);
  const [pendingOutcomeBalance, setPendingOutcomeBalance] = useState<
    number | undefined
  >(undefined);
  const [balanceLoading, setBalanceLoading] = useState<boolean>(false);

  const txHistory = useArrayState<API_CreamTransaction>([]);
  const [txHistoryLoading, setTxHistoryLoading] = useState(false);
  const [updatePendingLoading, setUpdatePendingLoading] = useState(false);

  useEffect(() => {
    if (!loggedIn) {
      txHistory.setValue([]);
    }
  }, [loggedIn]);

  const getBalance = async (): Promise<API_GetBalance | null> => {
    return new Promise((resolve) => {
      setBalanceLoading(true);
      fetcher.cream
        .getBalance()
        .then((response) => {
          setBalance(response.balance);
          setPendingIncomeBalance(response.pendingIncomeBalance);
          setPendingOutcomeBalance(response.pendingOutcomeBalance);
          resolve(response);
        })
        .catch((err: any) => {
          alert.showError(err);
          resolve(null);
        })
        .finally(() => setBalanceLoading(false));
    });
  };

  const getTransactionsHistory = () => {
    setTxHistoryLoading(true);
    fetcher.cream
      .getPendingTxs()
      .then((response) => txHistory.setValue(response))
      .catch(alert.showError)
      .finally(() => setTxHistoryLoading(false));
  };

  const updatePendingTransaction = (
    hash: string,
    stopLoading?: () => any
  ): Promise<TransactionStatuses> => {
    return new Promise((resolve, reject) => {
      fetcher.cream
        .updateTxStatus(hash)
        .then((response) => {
          resolve(response.transaction.status);
          switch (response.transaction.status) {
            case TransactionStatuses.CONFIRMED:
              {
                txHistory.replaceOne(
                  (t) => t.hash === hash,
                  (t) => ({ ...t, status: TransactionStatuses.CONFIRMED })
                );
                setBalance(response.balance);
                setPendingIncomeBalance(response.pendingIncomeBalance);
                setPendingOutcomeBalance(response.pendingOutcomeBalance);
              }
              break;
            case TransactionStatuses.FAILED:
              {
                txHistory.replaceOne(
                  (t) => t.hash === hash,
                  (t) => ({ ...t, status: TransactionStatuses.FAILED })
                );
                setBalance(response.balance);
                setPendingIncomeBalance(response.pendingIncomeBalance);
                setPendingOutcomeBalance(response.pendingOutcomeBalance);
              }
              break;
            default: {
              txHistory.replaceOne(
                (t) => t.hash === hash,
                (prev) => ({
                  ...prev,
                  updatedAt: response.transaction.updatedAt
                })
              );
              if (stopLoading) stopLoading();
            }
          }
        })
        .catch((err) => {
          reject(err);
          alert.showError(err);
          if (stopLoading) stopLoading();
        });
    });
  };

  const sellCream = (
    amount: number,
    onFinish: () => void,
    stopLoading: () => void
  ) => {
    fetcher.cream
      .sell(amount)
      .then((response) => {
        setBalance(response.balance);
        setPendingOutcomeBalance(response.pendingOutcomeBalance);
        txHistory.pushToBegin(response.transaction);
        alert.show('Transaction is successfully queued');
        onFinish();
      })
      .catch(alert.showError)
      .finally(() => stopLoading());
  };

  const buyCream = (state: BuyCreamState) => {
    connect()
      .then((result) => {
        proceedWithBuy(result.web3, result.publicAddress, state);
      })
      .catch((err) => {
        alert.showError(err);
        state.stopLoading();
      });
  };

  const proceedWithBuy = (
    web3: Web3,
    publicAddress: string,
    state: BuyCreamState
  ) => {
    const { eth, stopLoading } = state;
    const txAmount = eth * oneEther;
    //timeout needed cause if metamask was locked - it's closed _after_ we initiate transaction
    //e.g. user unlocks extension, transaction is initiated, metamask closes extension after unlock
    setTimeout(() => {
      web3.eth
        .sendTransaction({
          from: publicAddress,
          to: process.env.REACT_APP_GAME_ADDRESS,
          value: String(txAmount)
        })
        .then((receipt) => {
          if (receipt) {
            sendGetCreamRequest(receipt.transactionHash, state);
          } else {
            stopLoading();
            alert.showError("Can't complete the transaction! Please try again");
          }
        })
        .catch(() => {
          stopLoading();
          alert.showError("Can't complete the transaction! Please try again");
        });
    }, 500);
  };

  const sendGetCreamRequest = (hash: string, state: BuyCreamState) => {
    fetcher.cream
      .buy(hash, Number(state.cream))
      .then((response) => {
        state.onFinish();
        setPendingIncomeBalance(response.pendingIncomeBalance);
        txHistory.pushToBegin(response.transaction);
        alert.show('Transaction is successfully queued');
      })
      .catch(alert.showError)
      .finally(() => state.stopLoading());
  };

  return (
    <CreamContext.Provider
      value={{
        //BALANCE
        balance,
        pendingIncomeBalance,
        pendingOutcomeBalance,
        balanceLoading,
        getBalance,

        //TRANSACTIONS
        txHistory: txHistory.value,
        txHistoryLoading,
        getTransactionsHistory,
        updatePendingTransaction,
        updatePendingLoading,
        setUpdatePendingLoading,

        //CREAM
        buyCream,
        sellCream
      }}
    >
      {children}
    </CreamContext.Provider>
  );
};

const useCream = () => useContext(CreamContext);

export default useCream;
